import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { encryptData, decryptData, isEncrypted } from '../utils/encryption';

export function useProjects(user, isOnline) {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load and sync projects from Firestore (with encryption support)
  useEffect(() => {
    if (!user) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    // Load from localStorage first (with decryption)
    (async () => {
      try {
        const savedProjects = localStorage.getItem(`projects_${user.uid}`);
        if (savedProjects) {
          if (isEncrypted(savedProjects)) {
            const decrypted = await decryptData(savedProjects, user.uid);
            if (decrypted) setProjects(decrypted);
          } else {
            // Backward compatibility: unencrypted data
            setProjects(JSON.parse(savedProjects));
          }
        }
      } catch (error) {
        console.error('Error loading encrypted projects:', error);
      }
    })();

    if (!isOnline) {
      setIsLoading(false);
      return;
    }

    const q = query(
      collection(db, 'projects'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const firestoreProjects = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProjects(firestoreProjects);

      // Save encrypted to localStorage
      try {
        const encrypted = await encryptData(firestoreProjects, user.uid);
        localStorage.setItem(`projects_${user.uid}`, encrypted);
      } catch (error) {
        console.error('Error saving encrypted projects:', error);
      }

      setIsLoading(false);
    }, (error) => {
      console.error('Projects sync error:', error);
      setIsLoading(false);
    });

    return unsubscribe;
  }, [user, isOnline]);

  // Save to localStorage as backup (with encryption)
  useEffect(() => {
    if (user && projects.length > 0) {
      (async () => {
        try {
          const encrypted = await encryptData(projects, user.uid);
          localStorage.setItem(`projects_${user.uid}`, encrypted);
        } catch (error) {
          console.error('Error saving encrypted projects:', error);
        }
      })();
    }
  }, [projects, user]);

  const addProject = useCallback(async (name, color) => {
    if (!name.trim() || !user) return null;

    const project = {
      id: Date.now().toString(),
      name: name.trim(),
      color,
      userId: user.uid,
      createdAt: Date.now()
    };

    setProjects(prev => [...prev, project]);

    if (isOnline) {
      try {
        await setDoc(doc(db, 'projects', project.id), project);
      } catch (error) {
        console.error('Error adding project:', error);
      }
    }

    return project;
  }, [user, isOnline]);

  const updateProject = useCallback(async (projectId, updates) => {
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, ...updates } : p
    ));

    if (isOnline && user) {
      try {
        await updateDoc(doc(db, 'projects', projectId), {
          ...updates,
          updatedAt: Date.now()
        });
      } catch (error) {
        console.error('Error updating project:', error);
      }
    }
  }, [user, isOnline]);

  const deleteProject = useCallback(async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? Time entries will keep their project reference.')) {
      return;
    }

    setProjects(prev => prev.filter(p => p.id !== projectId));

    if (isOnline && user) {
      try {
        await deleteDoc(doc(db, 'projects', projectId));
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  }, [user, isOnline]);

  const getProjectById = useCallback((projectId) => {
    return projects.find(p => p.id === projectId) || { id: projectId || 'unknown', name: 'Unknown', color: '#94a3b8' };
  }, [projects]);

  return {
    projects,
    isLoading,
    addProject,
    updateProject,
    deleteProject,
    getProjectById
  };
}
